/**
 * URL + storage locale helpers for apex auth entry (/signin, /signup).
 *
 * Marketing and the console share origin `solvait.ai`, so localStorage
 * priming on CTA click is a reliable fallback when query params are
 * stripped (Clerk sub-routes, OAuth redirects).
 */

import type { Locale } from "../i18n";

const STORAGE_KEY = "solvait_locale";

/** Read `?lng=` from a search string. */
export function localeFromSearch(search: string): Locale | null {
  const raw = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  ).get("lng");
  return raw === "ar" || raw === "en" ? raw : null;
}

/** Read `?lng=` from the current window location. */
export function readLocaleFromWindow(): Locale | null {
  if (typeof window === "undefined") return null;
  return localeFromSearch(window.location.search);
}

/** Language to pass into `i18next.init({ lng })` before the detector runs. */
export function initialLanguageFromUrl(): Locale | undefined {
  return readLocaleFromWindow() ?? undefined;
}

/** Persist locale for the console SPA (matches i18next `lookupLocalStorage`). */
export function persistConsoleLocale(locale: Locale): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Private mode / blocked storage — query string remains the source.
  }
}
